import { startAppLoadSpan } from './sentry-init';
import './datadog-init';
// Holds page-wide rules (body, h1, [dir]) that every page relies on, so it loads with the boot
// stylesheet rather than with the editor.
import './components/LexicalEditor/editorStyles.css';
import { FronteggProvider } from '@frontegg/react';
import { GlobalStyles, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { enableDevValidations, ModuleRegistry } from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { Provider } from 'jotai';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router';
import { AccountGate } from './AccountGate';
import { AppLoader } from './AppLoader';
import { preloadPagesForPath, RoutedApp } from './AppRoutes2';
import './assets/fonts/fonts.css';
import './theme/web-common.css';
import { AuthWrapperFrontEgg } from './AuthWrapperFrontEgg';
import { DebugAuthProvider } from './debug/DebugAuthProvider';
import { isDebugMode } from './debug/debugUser';
import { BrowserGuard } from './components/BrowserGuard/BrowserGuard';
import { registerDynamicImportReloadListener } from './components/Fallback/GenericErrorFallback';
import { preloadShellForPath } from './NavWrapper';
import { RouteErrorFallback } from './components/Fallback/RouteErrorFallback';
import { LoadingBarProvider } from './components/LoadingBar/LoadingBarContext';
import { AgGridLicense, FrontEggContextOptions } from './constants/Licenses';
import { HydrateAtoms, queryClient } from './services/MaggieQueryClient';
import { globalStyles } from './theme/GlobalStyles';
import { greycroftTheme } from './theme/material-custom';
import { gridModules } from './util/ag-grid-modules';
import { noteFronteggRequest } from './util/earlyBoot';
import { getEnvVariable, isLocal } from './util/environment';
import { FeatureFlags, getFeatureFlagsDefaults } from './util/feature-flags';
import { getForesightStore } from './util/jotai-store';
import { initYup } from './util/yup-bootstrap';

// AG Grid 36 left validation out of AllCommunityModule; without it a misconfigured grid logs only an error
// code. Console only: the default also paints a diagnostics overlay over every grid. Dropped from prod builds.
if (import.meta.env.DEV) {
  enableDevValidations({ showOverlayOn: [] });
}
ModuleRegistry.registerModules(gridModules);
LicenseManager.setLicenseKey(AgGridLicense);
initYup();
startAppLoadSpan();
registerDynamicImportReloadListener();
// The landing page's chunk and its section nav download alongside the boot requests instead of
// after the full-page loader clears.
preloadShellForPath(window.location.pathname);
preloadPagesForPath(window.location.pathname);

const debugMode = isDebugMode();

const appContent = (
  <LoadingBarProvider>
    <ThemeProvider theme={greycroftTheme}>
      <GlobalStyles styles={globalStyles} />
      <BrowserGuard>
        {debugMode ? (
          <DebugAuthProvider>
            <AppLoader>
              <RoutedApp />
            </AppLoader>
          </DebugAuthProvider>
        ) : (
          <AuthWrapperFrontEgg>
            <AccountGate>
              <AppLoader>
                <RoutedApp />
              </AppLoader>
            </AccountGate>
          </AuthWrapperFrontEgg>
        )}
      </BrowserGuard>
    </ThemeProvider>
  </LoadingBarProvider>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path='/*'
      errorElement={<RouteErrorFallback />}
      element={
        debugMode ? (
          appContent
        ) : (
          <FronteggProvider
            contextOptions={{ ...FrontEggContextOptions, beforeRequestInterceptor: noteFronteggRequest }}
            hostedLoginBox={true}
            authOptions={{
              hostedLoginOptions: {
                loadUserOnFirstLoad: true,
              },
              // Frontegg's login box refreshes the token every 0.8 × its lifetime instead of logging the
              // user out when it expires; the session then lasts as long as Frontegg's session settings allow.
              // That code ships in Frontegg's CDN bundle, not the npm SDK, so re-check it after upgrading
              // @frontegg/react.
              keepSessionAlive: true,
            }}
            entitlementsOptions={{ enabled: true }}
          >
            {appContent}
          </FronteggProvider>
        )
      }
    ></Route>
  )
);

const root = createRoot(document.getElementById('root')!);

function Index() {
  return (
    <StrictMode>
      {/* see https://jotai.org/docs/extensions/query#referencing-the-same-instance-of-query-client-in-your-project */}
      <QueryClientProvider client={queryClient}>
        <Provider store={getForesightStore()}>
          <HydrateAtoms>
            <RouterProvider router={router} />
          </HydrateAtoms>
        </Provider>
      </QueryClientProvider>
    </StrictMode>
  );
}

// Local-dev flag overrides. Only applied when running the Vite dev server
// (isLocal()), never in built/deployed envs. Lets Document Hub and the Field
// Manager be exercised locally without configuring the flags in LaunchDarkly.
// Remove each once it is managed in the LD dashboard.
function getBootstrapFlags() {
  const flags = getFeatureFlagsDefaults();
  if (isLocal()) {
    flags[FeatureFlags.showDocumentHub] = true;
    flags[FeatureFlags.showFieldsManager] = true;
  }
  return flags;
}

const IndexWithLd = withLDProvider({
  clientSideID: getEnvVariable('VITE_LAUNCH_DARKLY_CLIENT_ID'),
  flags: getBootstrapFlags(),
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(Index);

root.render(<IndexWithLd />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log);
